// components/chat/FileAttachmentButton.jsx
import React, { useRef, useState } from 'react';
import fileService from '../../services/fileService';
import { useContacts } from '../../context/ContactsContext';  // ← CHANGE THIS
import { useNotification } from '../../hooks/useNotification';

export function FileAttachmentButton({ onFileSelected, disabled }) {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const { selectedContact } = useContacts();  // ← CHANGE THIS
  const { showNotification } = useNotification();

  const handleFileSelect = async (e) => {
    console.log('🔵 [FileAttachmentButton] File selected event triggered');
    
    const file = e.target.files[0];
    console.log('🔵 [FileAttachmentButton] File:', file ? {
      name: file.name,
      size: file.size,
      type: file.type
    } : 'No file selected');
    
    if (!file) {
      console.log('🔴 [FileAttachmentButton] No file selected');
      return;
    }

    // Reset input
    fileInputRef.current.value = '';
    console.log('🔵 [FileAttachmentButton] Input reset');

    // Check if contact is selected
    console.log('🔵 [FileAttachmentButton] Selected contact:', selectedContact);
    
    if (!selectedContact) {
      console.log('🔴 [FileAttachmentButton] No contact selected');
      showNotification('Please select a contact first', 'error');
      return;
    }

    if (!selectedContact.contactUuid) {
      console.log('🔴 [FileAttachmentButton] Contact has no UUID');
      showNotification('Invalid contact selected', 'error');
      return;
    }

    console.log('🔵 [FileAttachmentButton] Contact UUID:', selectedContact.contactUuid);

    // Validate file size (100MB max)
    const MAX_SIZE = 100 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      showNotification(`File too large. Maximum size is ${fileService.formatFileSize(MAX_SIZE)}`, 'error');
      return;
    }

    // Validate file type
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain', 'application/zip', 'audio/mpeg', 'audio/mp4',
      'video/mp4', 'video/quicktime'
    ];

    console.log('🔵 [FileAttachmentButton] File type:', file.type);
    
    if (!allowedTypes.includes(file.type)) {
      console.log('🔴 [FileAttachmentButton] File type not allowed:', file.type);
      showNotification('File type not allowed', 'error');
      return;
    }

    console.log('🟢 [FileAttachmentButton] File validation passed, starting upload...');
    setIsUploading(true);
    setUploadProgress({ stage: 'preparing', progress: 0 });

    try {
      console.log('🔵 [FileAttachmentButton] Calling fileService.uploadFullFile');
      
      const result = await fileService.uploadFullFile(
        selectedContact.contactUuid,
        file,
        (progress) => {
          console.log('📊 [FileAttachmentButton] Progress:', progress);
          setUploadProgress(progress);
        }
      );

      console.log('🟢 [FileAttachmentButton] Upload complete:', result);
      showNotification(`File "${file.name}" uploaded successfully`, 'success');
      
      // Pass file info to parent
      if (onFileSelected) {
        console.log('🔵 [FileAttachmentButton] Calling onFileSelected callback');
        onFileSelected({
          fileId: result.fileId,
          fileName: result.fileName,
          fileSize: result.fileSize,
          mimeType: result.mimeType
        });
      } else {
        console.log('🟡 [FileAttachmentButton] No onFileSelected callback provided');
      }

    } catch (error) {
      console.error('🔴 [FileAttachmentButton] Upload error:', error);
      console.error('🔴 [FileAttachmentButton] Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      showNotification(`Upload failed: ${error.message}`, 'error');
    } finally {
      console.log('🔵 [FileAttachmentButton] Cleaning up upload state');
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  const getProgressText = () => {
    if (!uploadProgress) return null;
    
    switch (uploadProgress.stage) {
      case 'preparing':
        return 'Preparing...';
      case 'uploading':
        return `Uploading ${Math.round(uploadProgress.progress)}%`;
      case 'encrypting':
        return 'Encrypting...';
      case 'complete':
        return 'Complete!';
      default:
        return null;
    }
  };

  const handleButtonClick = () => {
    console.log('🔵 [FileAttachmentButton] Button clicked');
    
    if (!selectedContact) {
      showNotification('Please select a contact first', 'warning');
      return;
    }
    
    fileInputRef.current?.click();
  };

  console.log('🔵 [FileAttachmentButton] Rendering, disabled:', disabled, 'isUploading:', isUploading, 'hasContact:', !!selectedContact);

  return (
    <div className="relative">
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        className="hidden"
        accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip,audio/mpeg,audio/mp4,video/mp4,video/quicktime"
        disabled={disabled || isUploading || !selectedContact}
      />
      
      <button
        type="button"
        onClick={handleButtonClick}
        disabled={disabled || isUploading || !selectedContact}
        className={`p-3 rounded-lg transition ${
          disabled || isUploading || !selectedContact
            ? 'bg-gray-300 cursor-not-allowed opacity-50'
            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
        }`}
        title={!selectedContact ? 'Select a contact first' : 'Attach file'}
      >
        {isUploading ? (
          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" />
          </svg>
        )}
      </button>
      
      {uploadProgress && (
        <div className="absolute bottom-full left-0 mb-2 bg-white shadow-lg rounded-lg p-3 min-w-[200px] z-10">
          <div className="text-sm font-medium mb-1">{getProgressText()}</div>
          {uploadProgress.stage === 'uploading' && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress.progress}%` }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}